/**
 * PublicUrlNotice — 로컬로 여셨을 때 **외부 주소로 가는 링크**를 띄웁니다.
 * (gauth v1.1, 2026-08-30)
 *
 * 🔴 왜 필요한가 — 회장님 지시(2026-08-30):
 *    *"일부 프로젝트는 로컬에서 접속하면 google auth 에 실패하고있음.
 *      만약 로컬에서 접속했으면, url 로 접속할 수 있는 링크도 배치할것.
 *      … 외부접속을 위해서는 url 을 우선해서 사용해야할 필요가 있음."*
 *
 *    실측 근거(감사로그, 2026-08-30):
 *      cdl    로컬 로그인 성공 **0건** / 차단 290건 · 외부(Caddy 경유) 성공 3건
 *      aitalk 로그인 성공 **0건**
 *    즉 **외부 URL 이 실제로 동작하는 경로**입니다. 로컬에서 막히셨을 때
 *    주소를 손으로 고쳐 치시게 두지 않습니다.
 *
 * 🔴 로컬이 아닐 때는 **아무것도 그리지 않습니다** — 외부에서 보고 계신데
 *    「외부로 가십시오」라고 하면 그것이 더 혼란스럽습니다.
 *
 * 붙이는 법 (의존성 0 — 어느 프로젝트에나 그대로 복사됩니다):
 *   1. 이 파일을 `components/PublicUrlNotice.tsx` 로 복사합니다
 *   2. `LoginGate.tsx` 의 `out`·`error` 단계 안에 한 줄 넣습니다:
 *        <PublicUrlNotice publicUrl="https://<서비스>.instapp.run" />
 *   3. 주소는 하드코딩해도 되고 `/api/auth/me` 응답의 `public_url` 을 써도 됩니다.
 */

/** 이 화면을 로컬 주소로 보고 계신가. 🔴 부분 문자열 매칭을 쓰지 않습니다. */
export function isLocalHost(host?: string): boolean {
  const h = host ?? (typeof window === 'undefined' ? '' : window.location.hostname)
  return h === '127.0.0.1' || h === 'localhost' || h === '::1' || h === '[::1]'
}

/** 지금 보고 계신 경로·쿼리·해시를 그대로 외부 주소에 이어 붙입니다. */
export function toPublicUrl(publicUrl: string, loc?: Location): string {
  const l = loc ?? (typeof window === 'undefined' ? undefined : window.location)
  const base = publicUrl.replace(/\/+$/, '')
  if (!l) return base
  return base + l.pathname + l.search + l.hash
}

export function PublicUrlNotice({
  publicUrl,
  className,
  compact,
}: {
  /** 이 서비스의 외부 주소. 없으면 아무것도 그리지 않습니다. */
  publicUrl?: string
  className?: string
  /** 로그인 화면이 아니라 상단 배너로 쓸 때 — 설명 문장을 줄입니다. */
  compact?: boolean
}) {
  if (!publicUrl) return null
  if (typeof window === 'undefined') return null
  if (!isLocalHost()) return null

  const target = toPublicUrl(publicUrl)
  const box = [
    'rounded-md border border-border bg-muted/40 px-3 py-2 text-left',
    compact ? '' : 'mt-4',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <div data-component="public-url-notice" className={box}>
      {!compact && (
        <p className="text-xs text-muted-foreground">
          로컬 주소에서는 Google 로그인이 막힐 수 있습니다. 외부 주소로 여시면 됩니다.
        </p>
      )}
      <a
        href={target}
        data-action="open-public-url"
        data-testid="public-url-link"
        className={[
          'inline-flex items-center gap-1.5 break-all rounded-md border border-border',
          'px-3 py-1.5 text-xs font-medium hover:bg-accent',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          // 🔴 모바일(360·412px) 터치 대상 최소 44×44px
          'max-sm:min-h-11',
          compact ? '' : 'mt-2',
        ].filter(Boolean).join(' ')}
      >
        <span aria-hidden>🔗</span>
        <span>{target}</span>
      </a>
    </div>
  )
}

export default PublicUrlNotice
